import React, { useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import CgInfoHint from 'modules/Common/ui/CgInfoHint';
import { useFieldEditing, FieldPresenceRing } from 'modules/Common/presence/FieldPresence';
import Label from './primitives/Label';
import ErrorText from './primitives/ErrorText';
import ControlAnchor from './primitives/ControlAnchor';
import { gapGroupProps, gapPartProps } from 'modules/Common/utils/gapAnchor';
import { useFormReadOnly } from './formReadOnly';

// Tailwind's JIT only emits classes that appear as complete strings in the
// source, so we can't template `col-span-${N}` directly — keep an explicit
// map. Default 12 = full width.
const COL_SPAN_CLASSES = {
  1: 'col-span-12 md:col-span-1',
  2: 'col-span-12 md:col-span-2',
  3: 'col-span-12 md:col-span-3',
  4: 'col-span-12 md:col-span-4',
  5: 'col-span-12 md:col-span-5',
  6: 'col-span-12 md:col-span-6',
  7: 'col-span-12 md:col-span-7',
  8: 'col-span-12 md:col-span-8',
  9: 'col-span-12 md:col-span-9',
  10: 'col-span-12 md:col-span-10',
  11: 'col-span-12 md:col-span-11',
  12: 'col-span-12',
};

// Wraps one form field: label + Controller-bound input + error message,
// plus an optional info popover trigger to the right of the label.
//
// The child must accept `field` + `fieldState` props and render the actual
// input. Field components in ./fields all conform to this contract.
//
// `hint` is an optional node rendered below the input/error (e.g. a
// "currently published: X — will be replaced" note) — free-supplier's
// draft-review form uses it to surface the proposed-vs-currently-published
// comparison inline, per field, instead of a separate table.
export default function FormField({
  name,
  label,
  description,
  required,
  colSpan = 12,
  className = '',
  popoverTitle,
  popoverContent,
  hidden,
  loading = false,
  hint,
  // ADDITIVE: the label stays for screen readers only — for a field whose
  // label is already on screen beside it (an inline edit in a labelled row).
  hideLabel = false,
  // Hold a validation error back until the operator has left the field once — for a field that
  // appears because of another answer (a justification revealed by a checkbox), where an error the
  // instant it appears scolds them for something they have not had the chance to do yet.
  errorAfterTouch = false,
  anchor = null,
  group = false,
  empty,
  children,
}) {
  const { control } = useFormContext();
  // P12/D30 — field-level editing presence (no-op with no FieldPresenceProvider). `name` is the
  // shared fieldKey; focus/change/blur publish this user's field, the ring shows a peer's.
  const fieldPresence = useFieldEditing(name);
  // `<Form readOnly>`, applied per field. The child's own `disabled` wins where
  // it has one, so this can only ever disable — never re-enable.
  const formReadOnly = useFormReadOnly();
  const rootRef = useRef(null);

  if (hidden) return null;

  const colClass = COL_SPAN_CLASSES[colSpan] || COL_SPAN_CLASSES[12];

  return (
    // onFocus/onBlur capture descendant-input focus (React bubbles focusin/out) → publish focus/blur.
    <div
      ref={rootRef}
      className={`${colClass} ${className}`.trim()}
      {...(anchor || group ? gapGroupProps : {})}
      onFocus={fieldPresence.onFocus}
      onBlur={fieldPresence.onBlur}
    >
      {label ? (
        <div
          className={hideLabel ? 'sr-only' : 'flex items-center gap-1.5 mb-1'}
          {...(hideLabel ? {} : gapPartProps)}
        >
          <Label
            htmlFor={name}
            required={required}
            className="mb-0"
          >
            {label}
          </Label>
          {popoverContent || popoverTitle ? (
            <CgInfoHint
              title={popoverTitle}
              position="top"
              subject={label}
            >
              {popoverContent}
            </CgInfoHint>
          ) : null}
          {loading ? (
            <FontAwesomeIcon
              icon={faSpinner}
              spin
              size="sm"
              className="text-primaryColor ml-auto"
            />
          ) : null}
        </div>
      ) : null}
      {description ? <p className="text-xs text-slate-500 mb-1.5 -mt-0.5">{description}</p> : null}
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: rawFieldState }) => {
          const fieldState =
            errorAfterTouch && !rawFieldState.isTouched
              ? { ...rawFieldState, error: undefined, invalid: false }
              : rawFieldState;
          return (
            <>
              <FieldPresenceRing fieldKey={name}>
                {React.cloneElement(children, {
                  // Publish "actively changing" on top of the field's own onChange (intense peer ring).
                  field: {
                    ...field,
                    onChange: (v) => {
                      field.onChange(v);
                      fieldPresence.onChange();
                    },
                  },
                  fieldState,
                  id: name,
                  disabled: children.props.disabled ?? formReadOnly,
                })}
              </FieldPresenceRing>
              {anchor ? (
                <ControlAnchor
                  rootRef={rootRef}
                  name={name}
                  anchor={anchor}
                  empty={empty}
                  value={field.value}
                />
              ) : null}
              <ErrorText>{fieldState.error?.message}</ErrorText>
            </>
          );
        }}
      />
      {hint ? <div className="mt-1">{hint}</div> : null}
    </div>
  );
}
